import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { apiFetch } from "./api";

const serviceTypes = ["AMC", "Repair", "Installation", "Generator", "Panels", "Supply"];
const projectStatuses = ["Planning", "Mobilization", "In Progress", "On Hold", "Completed", "Closed", "Cancelled"];
const priorities = ["Normal", "Urgent", "Critical"];
const paymentStatuses = ["Not Started", "Advance Pending", "Part Paid", "Fully Paid", "Overdue"];
const clientValueUsers = new Set([
  "kandarp.g@globusafriventures.com",
  "radhey.g@globusafriventures.com",
  "paschal.a@globusafriventures.com"
]);

const emptyProject = {
  project_name: "",
  service_type: "AMC",
  site_address: "",
  scope_description: "",
  project_value: "",
  start_date: "",
  expected_completion_date: "",
  actual_completion_date: "",
  status: "Planning",
  project_manager: "",
  assigned_engineer: "",
  vendor_or_sbc: "",
  priority: "Normal",
  payment_status: "Not Started",
  current_stage: "",
  next_action: "",
  remarks: ""
};

function ClientProjects({ user }) {
  const { id } = useParams();
  const [client, setClient] = useState(null);
  const [projects, setProjects] = useState([]);
  const [draft, setDraft] = useState(emptyProject);
  const canViewValues = clientValueUsers.has((user?.email || "").toLowerCase());

  function loadAll() {
    apiFetch(`/api/clients/${id}`).then(res => res.json()).then(setClient);
    apiFetch(`/api/clients/${id}/projects`)
      .then(res => res.json())
      .then(data => setProjects(Array.isArray(data) ? data : []));
  }

  useEffect(() => {
    loadAll();
  }, [id]);

  function updateDraft(field, value) {
    setDraft(prev => ({ ...prev, [field]: value }));
  }

  function startNew() {
    setDraft(emptyProject);
  }

  function saveProject() {
    apiFetch(`/api/clients/${id}/projects`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(draft)
    })
      .then(res => res.json())
      .then(data => {
        if (data.error) {
          alert(data.error);
          return;
        }
        startNew();
        loadAll();
      });
  }

  return (
    <div style={page}>
      <button onClick={() => (window.location.href = `/clients/${id}`)}>Back to Client</button>
      <h2>Project Lifecycle{client?.name ? ` - ${client.name}` : ""}</h2>

      <div style={card}>
        <h3>Start Project Lifecycle</h3>
        <ProjectForm draft={draft} updateDraft={updateDraft} canViewValues={canViewValues} />
        <button onClick={saveProject}>Create Project</button>
      </div>

      <table border="1" cellPadding="8" style={table}>
        <thead>
          <tr>
            <th>ID</th>
            <th>Project</th>
            <th>Service</th>
            <th>Status</th>
            <th>Stage</th>
            <th>Site</th>
            <th>Manager</th>
            <th>Engineer</th>
            <th>Payment</th>
            <th>Next Action</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          {projects.length > 0 ? projects.map(project => (
            <tr key={project.id}>
              <td>{displayTrackingId(project, client)}</td>
              <td>{project.project_name}</td>
              <td>{project.service_type}</td>
              <td>{project.status}</td>
              <td>{project.current_stage}</td>
              <td>{project.site_address}</td>
              <td>{project.project_manager}</td>
              <td>{project.assigned_engineer}</td>
              <td>{project.payment_status}</td>
              <td>{project.next_action}</td>
              <td>
                <button onClick={() => (window.location.href = `/clients/${id}/projects/${project.id}`)}>
                  Open Details
                </button>
              </td>
            </tr>
          )) : (
            <tr>
              <td colSpan="11">No project lifecycle records yet</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function compactCode(value, length) {
  const cleaned = String(value || "").toUpperCase().replace(/[^A-Z0-9]/g, "");
  return cleaned.slice(0, length) || "GEN";
}

function displayTrackingId(project, client) {
  if (project.tracking_id || project.trackingId) return project.tracking_id || project.trackingId;
  const baseId = project.source_sale_id || project.id;
  if (!baseId) return "";
  const sequence = String(Number(baseId) + 320).padStart(6, "0");
  return `GAVL-${compactCode(project.service_type, 3)}-${compactCode(client?.name, 6)}-${sequence}`;
}

function ProjectForm({ draft, updateDraft, canViewValues }) {
  return (
    <div style={formGrid}>
      <Field label="Project Name">
        <input placeholder="Project name" value={draft.project_name || ""} onChange={e => updateDraft("project_name", e.target.value)} />
      </Field>
      <Field label="Service Type">
        <select value={draft.service_type || "AMC"} onChange={e => updateDraft("service_type", e.target.value)}>
          {serviceTypes.map(type => <option key={type} value={type}>{type}</option>)}
        </select>
      </Field>
      <Field label="Site Address">
        <input placeholder="Site address" value={draft.site_address || ""} onChange={e => updateDraft("site_address", e.target.value)} />
      </Field>
      <Field label="Scope / Work Description">
        <textarea placeholder="Scope / work description" value={draft.scope_description || ""} onChange={e => updateDraft("scope_description", e.target.value)} />
      </Field>
      {canViewValues && (
        <Field label="Project Value">
          <input placeholder="Project value" value={draft.project_value || ""} onChange={e => updateDraft("project_value", e.target.value)} />
        </Field>
      )}
      <Field label="Start Date">
        <input type="date" value={draft.start_date || ""} onChange={e => updateDraft("start_date", e.target.value)} />
      </Field>
      <Field label="Expected Completion Date">
        <input type="date" value={draft.expected_completion_date || ""} onChange={e => updateDraft("expected_completion_date", e.target.value)} />
      </Field>
      <Field label="Actual Completion Date">
        <input type="date" value={draft.actual_completion_date || ""} onChange={e => updateDraft("actual_completion_date", e.target.value)} />
      </Field>
      <Field label="Project Status">
        <select value={draft.status || "Planning"} onChange={e => updateDraft("status", e.target.value)}>
          {projectStatuses.map(status => <option key={status} value={status}>{status}</option>)}
        </select>
      </Field>
      <Field label="Project Manager">
        <input placeholder="Project manager" value={draft.project_manager || ""} onChange={e => updateDraft("project_manager", e.target.value)} />
      </Field>
      <Field label="Assigned Engineer">
        <input placeholder="Assigned engineer" value={draft.assigned_engineer || ""} onChange={e => updateDraft("assigned_engineer", e.target.value)} />
      </Field>
      <Field label="Vendor / SBC">
        <input placeholder="Vendor / SBC" value={draft.vendor_or_sbc || ""} onChange={e => updateDraft("vendor_or_sbc", e.target.value)} />
      </Field>
      <Field label="Priority">
        <select value={draft.priority || "Normal"} onChange={e => updateDraft("priority", e.target.value)}>
          {priorities.map(priority => <option key={priority} value={priority}>{priority}</option>)}
        </select>
      </Field>
      <Field label="Payment Status">
        <select value={draft.payment_status || "Not Started"} onChange={e => updateDraft("payment_status", e.target.value)}>
          {paymentStatuses.map(status => <option key={status} value={status}>{status}</option>)}
        </select>
      </Field>
      <Field label="Current Stage / Milestone">
        <input placeholder="Current stage / milestone" value={draft.current_stage || ""} onChange={e => updateDraft("current_stage", e.target.value)} />
      </Field>
      <Field label="Next Action">
        <textarea placeholder="Next action" value={draft.next_action || ""} onChange={e => updateDraft("next_action", e.target.value)} />
      </Field>
      <Field label="Remarks">
        <textarea placeholder="Remarks" value={draft.remarks || ""} onChange={e => updateDraft("remarks", e.target.value)} />
      </Field>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label style={fieldLabel}>
      <span>{label}</span>
      {children}
    </label>
  );
}

const page = { padding: "20px" };
const card = { border: "1px solid #ddd", padding: "15px", margin: "20px 0", borderRadius: "8px", backgroundColor: "#f9f9f9" };
const formGrid = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: "10px", marginBottom: "12px" };
const table = { width: "100%", borderCollapse: "collapse" };
const fieldLabel = { display: "grid", gap: "4px", fontSize: "14px", fontWeight: "bold", textAlign: "left" };

export default ClientProjects;
